import { useEffect } from "react";
import { useAuth } from "@/components/auth/AuthProvider";
import { useGuestSaves } from "./use-guest-saves";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export function useGuestSavesMigration() {
  const { user } = useAuth();
  const { guestSaves, clearAllSaves } = useGuestSaves();

  useEffect(() => {
    const migrateGuestSavesToLeads = async () => {
      if (!user || guestSaves.length === 0) return;

      try {
        // Check if this migration has already been done
        const hasCompletedMigration = sessionStorage.getItem(`migration_completed_${user.id}`);
        if (hasCompletedMigration) return;

        console.log("Migrating guest saves to nursing home leads:", guestSaves);

        // Create nursing home leads for each saved home
        const leadsToInsert = guestSaves.map(save => ({
          user_id: user.id,
          nursing_home_id: save.nh_slug,
          nursing_home_name: save.center_name,
          location: save.county_name,
          status: 'interested',
          notes: `Migrated from saved favorites on ${new Date().toLocaleDateString()}`
        }));

        const { error: leadsError } = await supabase
          .from('nursing_home_leads')
          .insert(leadsToInsert);

        if (leadsError) {
          console.error("Error creating nursing home leads:", leadsError);
          throw leadsError;
        }

        // Send notification email about the new leads
        try {
          const { error: emailError } = await supabase.functions.invoke('send-transactional-email', {
            body: {
              template: 'nursing-home-leads',
              to: user.email,
              data: {
                firstName: user.user_metadata?.first_name || 'there',
                nursingHomes: guestSaves.map(save => save.center_name),
                dashboardUrl: `${window.location.origin}/dashboard`
              }
            }
          });

          if (emailError) {
            console.error("Error sending leads email:", emailError);
          }
        } catch (emailError) {
          console.error("Error sending email notification:", emailError);
        }

        // Clear guest saves and mark migration as complete
        clearAllSaves();
        sessionStorage.setItem(`migration_completed_${user.id}`, 'true');

        toast.success("Your saved nursing homes have been added to your dashboard!", {
          description: `${guestSaves.length} nursing home${guestSaves.length > 1 ? 's' : ''} added to your leads.`
        });

      } catch (error) {
        console.error("Error migrating guest saves:", error);
        toast.error("Failed to migrate your saved nursing homes", {
          description: "Please try refreshing the page or contact support."
        });
      }
    };

    migrateGuestSavesToLeads();
  }, [user, guestSaves, clearAllSaves]);
}